/**
 * Integration Service
 *
 * Unified API service for bank integrations using the new /integrations/:provider endpoints.
 * Replaces the legacy BankService with a RESTful architecture.
 */

import { apiClient } from '../client';
import type { BankStatus, BankAccount, BankTransaction } from '../types';

export interface Integration {
  id: string;
  name: string;
  description: string;
  connected: boolean;
  icon?: string;
  color?: string;
}

export interface IntegrationStatus extends BankStatus {
  accounts?: BankAccount[];
  connected_at?: string;
  merchant_name?: string;
  account_ids?: string[];
}

export interface IntegrationInfo {
  id: string;
  name: string;
  connected: boolean;
}

export interface IntegrationListResponse {
  integrations: IntegrationInfo[];
}

export class IntegrationService {
  /**
   * List all available integrations (no org context needed)
   */
  async listIntegrations(): Promise<IntegrationInfo[]> {
    const response = await apiClient.get<IntegrationListResponse>('/integrations');
    return response.integrations;
  }

  /**
   * Get integration connection status
   */
  async getStatus(provider: string, organizationId: string): Promise<IntegrationStatus> {
    return apiClient.get<IntegrationStatus>(
      `/integrations/${provider}/status?organization_id=${encodeURIComponent(organizationId)}`
    );
  }

  /**
   * Get OAuth authorization URL for provider
   */
  async getAuthUrl(provider: string, organizationId: string, options?: Record<string, string>): Promise<string> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (options) {
      for (const [key, value] of Object.entries(options)) {
        params.append(key, value);
      }
    }
    const response = await apiClient.get<{ auth_url: string }>(
      `/integrations/${provider}/auth-url?${params.toString()}`
    );
    return response.auth_url;
  }

  /**
   * Disconnect an integration
   */
  async disconnect(provider: string, organizationId: string): Promise<void> {
    await apiClient.delete(
      `/integrations/${provider}?organization_id=${encodeURIComponent(organizationId)}`
    );
  }

  /**
   * List accounts for a bank integration
   */
  async getAccounts(provider: string, organizationId: string): Promise<BankAccount[]> {
    const response = await apiClient.get<{ accounts: BankAccount[] }>(
      `/integrations/${provider}/accounts?organization_id=${encodeURIComponent(organizationId)}`
    );
    return response.accounts;
  }

  /**
   * Get transactions for a provider
   */
  async getTransactions(
    provider: string,
    organizationId: string,
    options?: {
      startDate?: string;
      endDate?: string;
      accountId?: string;
    }
  ): Promise<BankTransaction[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (options?.startDate) params.append('start_date', options.startDate);
    if (options?.endDate) params.append('end_date', options.endDate);
    if (options?.accountId) params.append('account_id', options.accountId);

    const response = await apiClient.get<{ transactions: BankTransaction[] }>(
      `/integrations/${provider}/transactions?${params.toString()}`
    );
    return response.transactions;
  }

  /**
   * Fetch one page of transactions from provider and import them to the DB.
   * Returns pagination cursor for the next page.
   */
  async fetchAndImportPage(
    provider: string,
    organizationId: string,
    options?: {
      startDate?: string;
      endDate?: string;
      accountId?: string;
      cursor?: string;
      pageSize?: number;
    }
  ): Promise<{
    transactions: BankTransaction[];
    imported: number;
    duplicates: number;
    errors: number;
    firstError?: string;
    nextCursor: string | null;
    hasMore: boolean;
  }> {
    const params = new URLSearchParams({ organization_id: organizationId, import: 'true' });
    if (options?.startDate) params.append('start_date', options.startDate);
    if (options?.endDate) params.append('end_date', options.endDate);
    if (options?.accountId) params.append('account_id', options.accountId);
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.pageSize) params.append('page_size', String(options.pageSize));

    const response = await apiClient.get<{
      transactions: BankTransaction[];
      imported: number;
      duplicates: number;
      errors: number;
      first_error?: string;
      next_cursor: string | null;
      has_more: boolean;
    }>(`/integrations/${provider}/transactions?${params.toString()}`, { retry: false });

    return {
      transactions: response.transactions,
      imported: response.imported ?? 0,
      duplicates: response.duplicates ?? 0,
      errors: response.errors ?? 0,
      firstError: response.first_error,
      nextCursor: response.next_cursor ?? null,
      hasMore: response.has_more ?? false,
    };
  }

  /**
   * @deprecated Use fetchAndImportPage for paginated progress support
   */
  async fetchAndImport(
    provider: string,
    organizationId: string,
    options?: {
      startDate?: string;
      endDate?: string;
      accountId?: string;
    }
  ): Promise<{ transactions: BankTransaction[]; imported: number; duplicates: number; errors: number }> {
    const result = await this.fetchAndImportPage(provider, organizationId, options);
    return {
      transactions: result.transactions,
      imported: result.imported,
      duplicates: result.duplicates,
      errors: result.errors,
    };
  }

  /**
   * Import transactions from an integration
   */
  async importTransactions(
    provider: string,
    organizationId: string,
    transactionIds: string[]
  ): Promise<void> {
    await apiClient.post(`/integrations/${provider}/import`, {
      organization_id: organizationId,
      transaction_ids: transactionIds,
    });
  }
}

// Export singleton instance
export const integrationService = new IntegrationService();
